/**
 * Appointment write helpers — web SDK equivalents of
 * `src/features/crm/appointments.ts`.
 *
 * Top-level `appointments` collection; rules gate writes to anyone
 * who `canMarkAttendance` (CRM-write + site engineers/supervisors).
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';
import type {
  AppointmentStatus,
  AppointmentType,
  LocationMode,
  MeetingPurpose,
} from '@/lib/useAppointments';

export type CreateAppointmentInput = {
  orgId: string;
  type: AppointmentType;
  title: string;
  scheduledAt: Date;
  status: AppointmentStatus;
  createdBy: string;

  /** NEW per redesigned form — what the meeting is about. */
  meetingPurpose?: MeetingPurpose;
  /** NEW per redesigned form — where the meeting happens. */
  locationMode?: LocationMode;
  leadId?: string;
  clientName?: string;
  clientPhone?: string;
  clientAddress?: string;
  durationMins?: number;
  location?: string;
  attendees?: string[];
  /** NEW per redesigned form — display name of the assigned staff. */
  assignedToName?: string;
  notes?: string;
  outcome?: string;
};

export async function createAppointment(input: CreateAppointmentInput): Promise<string> {
  const doc: Record<string, unknown> = {
    orgId: input.orgId,
    type: input.type,
    title: input.title,
    scheduledAt: Timestamp.fromDate(input.scheduledAt),
    status: input.status,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (input.meetingPurpose) doc.meetingPurpose = input.meetingPurpose;
  if (input.locationMode) doc.locationMode = input.locationMode;
  if (input.leadId) doc.leadId = input.leadId;
  if (input.clientName) doc.clientName = input.clientName;
  if (input.clientPhone) doc.clientPhone = input.clientPhone;
  if (input.clientAddress) doc.clientAddress = input.clientAddress;
  if (input.durationMins !== undefined) doc.durationMins = input.durationMins;
  if (input.location) doc.location = input.location;
  if (input.attendees && input.attendees.length > 0) doc.attendees = input.attendees;
  if (input.assignedToName) doc.assignedToName = input.assignedToName;
  if (input.notes) doc.notes = input.notes;
  if (input.outcome) doc.outcome = input.outcome;

  const ref = await addDoc(collection(db, 'appointments'), doc);
  return ref.id;
}

export type UpdateAppointmentInput = Partial<{
  leadId: string | null;
  clientName: string | null;
  clientPhone: string | null;
  clientAddress: string | null;
  type: AppointmentType;
  meetingPurpose: MeetingPurpose | null;
  locationMode: LocationMode | null;
  title: string;
  scheduledAt: Date | null;
  durationMins: number | null;
  location: string | null;
  attendees: string[] | null;
  assignedToName: string | null;
  status: AppointmentStatus;
  notes: string | null;
  outcome: string | null;
}>;

export async function updateAppointment(
  id: string,
  data: UpdateAppointmentInput,
): Promise<void> {
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (data.leadId !== undefined) {
    updates.leadId = data.leadId === null ? deleteField() : data.leadId;
  }
  if (data.clientName !== undefined) {
    updates.clientName = data.clientName === null ? deleteField() : data.clientName;
  }
  if (data.clientPhone !== undefined) {
    updates.clientPhone = data.clientPhone === null ? deleteField() : data.clientPhone;
  }
  if (data.clientAddress !== undefined) {
    updates.clientAddress = data.clientAddress === null ? deleteField() : data.clientAddress;
  }
  if (data.type !== undefined) updates.type = data.type;
  if (data.meetingPurpose !== undefined) {
    updates.meetingPurpose =
      data.meetingPurpose === null ? deleteField() : data.meetingPurpose;
  }
  if (data.locationMode !== undefined) {
    updates.locationMode =
      data.locationMode === null ? deleteField() : data.locationMode;
  }
  if (data.title !== undefined) updates.title = data.title;
  if (data.scheduledAt !== undefined) {
    updates.scheduledAt =
      data.scheduledAt === null ? deleteField() : Timestamp.fromDate(data.scheduledAt);
  }
  if (data.durationMins !== undefined) {
    updates.durationMins =
      data.durationMins === null ? deleteField() : data.durationMins;
  }
  if (data.location !== undefined) {
    updates.location = data.location === null ? deleteField() : data.location;
  }
  if (data.attendees !== undefined) {
    updates.attendees =
      data.attendees === null || data.attendees.length === 0
        ? deleteField()
        : data.attendees;
  }
  if (data.assignedToName !== undefined) {
    updates.assignedToName =
      data.assignedToName === null ? deleteField() : data.assignedToName;
  }
  if (data.status !== undefined) updates.status = data.status;
  if (data.notes !== undefined) {
    updates.notes = data.notes === null ? deleteField() : data.notes;
  }
  if (data.outcome !== undefined) {
    updates.outcome = data.outcome === null ? deleteField() : data.outcome;
  }

  await updateDoc(doc(db, 'appointments', id), updates);
}

export async function deleteAppointment(id: string): Promise<void> {
  await deleteDoc(doc(db, 'appointments', id));
}
